import React, { useState, useMemo } from "react";
import { X, Target, Flag, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useSafeBackdropClose } from "../lib/modal";
import { fmtDateGB } from "../lib/format";
import DateInput from "./DateInput";

/**
 * Unified modal to create/edit Tasks, Milestones and Goals.
 * - kind: "task" | "milestone" | "goal" (initial)
 * - existing: undefined for new, or task object for edit
 */
export default function TaskModal({ projectId, existing, defaultKind = "task", tasks = [], users = [], onClose, onSaved }) {
  const isEdit = !!existing;

  const initialKind = isEdit
    ? (existing.goal ? "goal" : existing.milestone ? "milestone" : "task")
    : defaultKind;

  const [form, setForm] = useState({
    name: existing?.name || "",
    description: existing?.description || "",
    start_date: existing?.start_date || todayISO(),
    end_date: existing?.end_date || addDaysISO(7),
    status: existing?.status || "not_started",
    progress_pct: existing?.progress_pct ?? 0,
    assignee: existing?.assignee || "",
    dependencies: existing?.dependencies || [],
    kind: initialKind,
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleDep = (id) => {
    setForm((f) => ({
      ...f,
      dependencies: f.dependencies.includes(id)
        ? f.dependencies.filter((x) => x !== id)
        : [...f.dependencies, id],
    }));
  };

  const dependencyOptions = useMemo(
    () => tasks.filter((t) => !existing || t.id !== existing.id),
    [tasks, existing]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.kind !== "goal" && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setBusy(true);
    try {
      const isMilestone = form.kind === "milestone";
      const isGoal = form.kind === "goal";
      const sameDates = isMilestone || isGoal;
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        start_date: form.start_date,
        end_date: sameDates ? form.start_date : form.end_date,
        status: form.status,
        progress_pct: form.kind === "milestone" && form.status === "done" ? 100 : form.progress_pct,
        assignee: form.assignee || null,
        dependencies: form.dependencies,
        milestone: isMilestone,
        goal: isGoal,
      };
      if (isEdit) {
        await api.patch(`/tasks/${existing.id}`, payload);
        toast.success(`${labelFor(form.kind)} updated`);
      } else {
        await api.post("/tasks", { project_id: projectId, ...payload });
        toast.success(`${labelFor(form.kind)} created`);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" {...useSafeBackdropClose(onClose)} data-testid="task-modal">
      <form
        className="modal-card p-6"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="task-modal-form"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">{isEdit ? "Edit" : "New"}</div>
            <div className="font-display text-xl font-semibold mt-1">
              {labelFor(form.kind)}
            </div>
          </div>
          <button type="button" onClick={onClose} className="muted" data-testid="task-modal-close">
            <X size={18} />
          </button>
        </div>

        {/* Kind switcher */}
        <div className="grid grid-cols-3 gap-2 mb-5" data-testid="task-kind-picker">
          {[
            { k: "task", icon: ListChecks, t: "Task" },
            { k: "milestone", icon: Flag, t: "Milestone" },
            { k: "goal", icon: Target, t: "Goal" },
          ].map(({ k, icon: Icon, t }) => (
            <button
              key={k}
              type="button"
              onClick={() => setForm((f) => ({ ...f, kind: k }))}
              data-testid={`task-kind-${k}`}
              className={`p-3 rounded-md border text-xs font-medium flex flex-col items-center gap-1 transition-colors ${
                form.kind === k
                  ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-hover)]"
                  : "border-[var(--line)] hover:border-[var(--accent)]"
              }`}
            >
              <Icon size={16} />
              {t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="label block mb-1">Name *</span>
            <input
              required
              autoFocus
              className="input-swiss"
              value={form.name}
              onChange={upd("name")}
              data-testid="task-name-input"
            />
          </label>

          <label className="block md:col-span-2">
            <span className="label block mb-1">Description</span>
            <textarea
              className="input-box"
              rows={2}
              value={form.description}
              onChange={upd("description")}
              data-testid="task-description-input"
            />
          </label>

          <label className="block">
            <span className="label block mb-1">{form.kind === "goal" ? "Target date" : "Start date"} *</span>
            <DateInput
              className="input-swiss"
              value={form.start_date}
              onChange={upd("start_date")}
              required
              data-testid="task-start-input"
            />
          </label>

          {form.kind === "task" && (
            <label className="block">
              <span className="label block mb-1">End date *</span>
              <DateInput
                className="input-swiss"
                value={form.end_date}
                onChange={upd("end_date")}
                required
                data-testid="task-end-input"
              />
            </label>
          )}

          <label className="block">
            <span className="label block mb-1">Status</span>
            <select className="input-box" value={form.status} onChange={upd("status")} data-testid="task-status-input">
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="blocked">Blocked</option>
              <option value="done">Done</option>
            </select>
          </label>

          {form.kind !== "milestone" && form.kind !== "goal" && (
            <label className="block">
              <span className="label block mb-1">Progress %</span>
              <input
                type="number"
                min={0}
                max={100}
                className="input-swiss"
                value={form.progress_pct}
                onChange={upd("progress_pct")}
                data-testid="task-progress-input"
              />
            </label>
          )}

          <label className="block md:col-span-2">
            <span className="label block mb-1">Assignee</span>
            <select
              className="input-box"
              value={form.assignee}
              onChange={upd("assignee")}
              data-testid="task-assignee-input"
            >
              <option value="">— unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.email} · {u.role}</option>
              ))}
            </select>
          </label>

          {dependencyOptions.length > 0 && form.kind !== "goal" && (
            <div className="md:col-span-2">
              <div className="label mb-2">Dependencies (must finish first)</div>
              <div className="max-h-32 overflow-y-auto border border-[var(--line)] rounded-md p-3 space-y-1.5 bg-[var(--surface)]">
                {dependencyOptions.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.dependencies.includes(t.id)}
                      onChange={() => toggleDep(t.id)}
                      data-testid={`task-dep-${t.id}`}
                    />
                    <span className="font-mono text-[10px] muted">{fmtDateGB(t.start_date)}</span>
                    <span className="truncate">{t.milestone ? "◆ " : t.goal ? "★ " : ""}{t.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="task-modal-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="task-modal-save">
            {busy ? "Saving…" : isEdit ? "Save changes" : `Create ${labelFor(form.kind).toLowerCase()}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function labelFor(k) {
  return k === "milestone" ? "Milestone" : k === "goal" ? "Goal" : "Task";
}
